import { Ref, ref } from "vue";
import { Member } from "../utils.js";

const MemberCache = new Map<string, Member | null>();
const MemberWaiters = new Map<string, Set<Ref<Member | null | undefined>>>();
const PendingIds = new Set<string>();

let scheduledFetch: number | null = null;

function flushPendingMembers() {
    scheduledFetch = null;

    const ids = [...PendingIds].filter((id) => !MemberCache.has(id));
    PendingIds.clear();

    if (!ids.length)
        return;

    fetch("/api/members", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(ids),
    })
        .then((res) => res.json())
        .then((members: Member[]) => {
            for (const member of members)
                if (member)
                    MemberCache.set(member.id, member);

            for (const id of ids)
                if (!MemberCache.has(id))
                    MemberCache.set(id, null);
        })
        .catch(() => {
            for (const id of ids)
                MemberCache.set(id, null);
        })
        .finally(() => {
            for (const id of ids) {
                const value = MemberCache.get(id);
                const waiters = MemberWaiters.get(id);

                if (!waiters)
                    continue;

                for (const waiter of waiters)
                    waiter.value = value;

                MemberWaiters.delete(id);
            }
        });
}

function scheduleMemberFetch(id: string) {
    PendingIds.add(id);

    if (scheduledFetch !== null)
        return;

    scheduledFetch = window.setTimeout(flushPendingMembers, 25);
}

export function useCachedMember(id: string): Ref<Member | null | undefined> {
    const cached = MemberCache.get(id);
    const member = ref<Member | null | undefined>(cached);

    if (cached !== undefined)
        return member;

    const waiters = MemberWaiters.get(id) ?? new Set<Ref<Member | null | undefined>>();

    waiters.add(member);
    MemberWaiters.set(id, waiters);
    scheduleMemberFetch(id);

    return member;
}
